import type { CompassDoc, PlotInfo } from "../net/api";

export type MarkerKind = "estate" | "library" | "mark";
export interface CompassMarker {
  id: string; kind: MarkerKind; pos: [number, number]; name?: string;
  bounds?: { min: [number, number]; max: [number, number] };
}
export interface PlacedMarker { id: string; kind: MarkerKind; t: number; clamped: boolean; label: string }

export const FIELD_DEG = 90; // the compass shows ±90° around the facing direction
export const LABEL_CHARS = 8;
export const TAPE_FROM = -360; // the tick tape spans three turns, so any yaw's window is on it
export const TAPE_TO = 720;

/** Angle in (−180, 180]. */
export function wrapDeg(a: number): number {
  const r = ((a % 360) + 360) % 360;
  return r > 180 ? r - 360 : r;
}

/** Bearing of (dx, dz) in degrees: 0 north (+z), 90 east (+x), the same as the camera yaw (spec 2e §2.2). */
export function bearing(dx: number, dz: number): number {
  return (Math.atan2(dx, dz) * 180) / Math.PI;
}

function label(m: CompassMarker, player: { x: number; z: number }, dist: number): string {
  if (m.kind === "library") return "";
  if (m.kind === "mark") {
    const chars = Array.from(m.name ?? "");
    return chars.length > LABEL_CHARS ? chars.slice(0, LABEL_CHARS).join("") + "…" : chars.join("");
  }
  const b = m.bounds;
  if (b && player.x >= b.min[0] && player.x <= b.max[0] && player.z >= b.min[1] && player.z <= b.max[1]) return "在領地內";
  return `${Math.round(dist)} m`;
}

/** Where each marker sits on the compass: t in [−1, 1] across its width, pinned to an end outside ±90°. */
export function compassLayout(yawDeg: number, player: { x: number; z: number }, markers: readonly CompassMarker[]): PlacedMarker[] {
  return markers.map((m) => {
    const dx = m.pos[0] - player.x, dz = m.pos[1] - player.z, dist = Math.hypot(dx, dz);
    let t = 0, clamped = false;
    if (dist >= 0.5) {
      const rel = wrapDeg(bearing(dx, dz) - yawDeg);
      if (Math.abs(rel) >= 179) { t = 1; clamped = true; } // right behind: always the right end, so it never flickers
      else if (Math.abs(rel) > FIELD_DEG) { t = Math.sign(rel); clamped = true; }
      else t = rel / FIELD_DEG;
    }
    return { id: m.id, kind: m.kind, t, clamped, label: label(m, player, dist) };
  });
}

/** CSS `left` of the tick tape, in % of the compass width, so the facing direction sits in the middle. */
export function tapeLeftPercent(yawDeg: number): number {
  const y = ((yawDeg % 360) + 360) % 360;
  return 50 - ((y - TAPE_FROM) * 100) / (2 * FIELD_DEG);
}

/** The markers to show: my estate, the library, then my own marks, minus hidden built-ins (spec 2e §2.3). */
export function compassMarkers(plots: readonly PlotInfo[], myId: string | null,
  landmarks: readonly { type: string; pos: [number, number] }[], doc: CompassDoc): CompassMarker[] {
  const out: CompassMarker[] = [];
  const estate = myId ? plots.find((p) => p.kind === "estate" && p.owner_id === myId) : undefined;
  if (estate && !doc.hide.includes("estate")) {
    const { min, max } = estate.bounds;
    out.push({ id: "estate", kind: "estate", pos: [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2], bounds: estate.bounds });
  }
  const library = landmarks.find((l) => l.type === "library");
  if (library && !doc.hide.includes("library")) out.push({ id: "library", kind: "library", pos: library.pos });
  for (const m of doc.marks) out.push({ id: `mark:${m.id}`, kind: "mark", pos: m.pos, name: m.name });
  return out;
}
